import { isInteractiveTarget } from "../core/math.js";

export class AmbientInteractions {
  #config;
  #scene;
  #store;
  #mistButton;
  #windButton;
  #cleanup = [];
  #trailCount = 0;
  #leafCount = 0;
  #lastPointer = { x: 0, y: 0, time: performance.now() };
  #idleTimer = 0;
  #windTimer = 0;
  #mistDense = false;
  #tier = "medium";
  #windSection = null;

  constructor({ config, scene, store, mistButton, windButton }) {
    this.#config = config;
    this.#scene = scene;
    this.#store = store;
    this.#mistButton = mistButton;
    this.#windButton = windButton;
  }

  init(tier) {
    this.#tier = tier;
    if (tier === "reduced") {
      this.#mistButton.hidden = true;
      this.#windButton.hidden = true;
      return;
    }

    this.#mistButton.hidden = false;
    this.#windButton.hidden = false;

    if (tier !== "low") {
      const pointerHandler = (event) => this.#onPointerMove(event);
      window.addEventListener("pointermove", pointerHandler, {
        passive: true
      });
      this.#cleanup.push(() =>
        window.removeEventListener("pointermove", pointerHandler)
      );
    }
    this.#cleanup.push(
      this.#store.on("performance:downgrade", (event) => {
        this.#tier = event.detail.performance.tier;
      })
    );

    if (this.#config.mistModes) {
      const mistHandler = () => this.#toggleMist();
      this.#mistButton.addEventListener("click", mistHandler);
      this.#cleanup.push(() =>
        this.#mistButton.removeEventListener("click", mistHandler)
      );
    } else {
      this.#mistButton.hidden = true;
    }

    if (this.#config.windGestures) {
      const windHandler = () => this.triggerWind();
      const keyHandler = (event) => {
        if (
          event.key.toLowerCase() === "w" &&
          !isInteractiveTarget(event.target)
        ) {
          this.triggerWind();
        }
      };
      this.#windButton.addEventListener("click", windHandler);
      window.addEventListener("keydown", keyHandler);
      this.#cleanup.push(
        () => this.#windButton.removeEventListener("click", windHandler),
        () => window.removeEventListener("keydown", keyHandler)
      );
    } else {
      this.#windButton.hidden = true;
    }

    if (this.#config.idleMode) {
      const activityHandler = () => this.#resetIdle();
      ["pointermove", "keydown", "scroll", "touchstart"].forEach(
        (eventName) =>
          window.addEventListener(eventName, activityHandler, {
            passive: true
          })
      );
      this.#cleanup.push(() => {
        ["pointermove", "keydown", "scroll", "touchstart"].forEach(
          (eventName) =>
            window.removeEventListener(eventName, activityHandler)
        );
      });
      this.#resetIdle();
    }
  }

  #onPointerMove(event) {
    if (["low", "reduced"].includes(this.#tier)) {
      return;
    }
    const now = performance.now();
    const elapsed = Math.max(16, now - this.#lastPointer.time);
    const deltaX = event.clientX - this.#lastPointer.x;
    const deltaY = event.clientY - this.#lastPointer.y;
    const velocity = Math.hypot(deltaX, deltaY) / elapsed * 1000;

    if (
      this.#config.freeLook &&
      !isInteractiveTarget(event.target)
    ) {
      const x = (event.clientX / window.innerWidth - 0.5) * -2;
      const y = (event.clientY / window.innerHeight - 0.5) * -2;
      document.documentElement.style.setProperty(
        "--video-x",
        `${x}px`
      );
      document.documentElement.style.setProperty(
        "--video-y",
        `${y}px`
      );
      document.documentElement.style.setProperty(
        "--mist-x",
        `${x * 3}px`
      );
    }

    if (
      this.#config.cursorTrail &&
      this.#tier === "high" &&
      velocity > 950 &&
      this.#trailCount < 8
    ) {
      this.#createTrail(event.clientX, event.clientY, -deltaX, -deltaY);
    }
    if (
      this.#config.leafSwipe &&
      this.#tier === "high" &&
      velocity > 1700 &&
      this.#leafCount < 3 &&
      Math.random() > 0.72
    ) {
      this.#createLeaf(event.clientX, event.clientY, deltaX);
    }
    this.#lastPointer = { x: event.clientX, y: event.clientY, time: now };
  }

  #createTrail(x, y, deltaX, deltaY) {
    const trail = document.createElement("span");
    trail.className = "cursor-trail";
    trail.style.left = `${x}px`;
    trail.style.top = `${y}px`;
    trail.style.setProperty("--trail-x", `${deltaX * 0.7}px`);
    trail.style.setProperty("--trail-y", `${deltaY * 0.7}px`);
    this.#trailCount += 1;
    document.body.append(trail);
    trail.addEventListener(
      "animationend",
      () => {
        trail.remove();
        this.#trailCount -= 1;
      },
      { once: true }
    );
  }

  #createLeaf(x, y, deltaX) {
    const leaf = document.createElement("span");
    leaf.className = "swipe-leaf";
    leaf.style.left = `${x}px`;
    leaf.style.top = `${y}px`;
    leaf.style.setProperty(
      "--leaf-x",
      `${Math.sign(deltaX || 1) * (25 + Math.random() * 35)}px`
    );
    this.#leafCount += 1;
    document.body.append(leaf);
    leaf.addEventListener(
      "animationend",
      () => {
        leaf.remove();
        this.#leafCount -= 1;
      },
      { once: true }
    );
  }

  triggerWind() {
    if (!["low", "reduced"].includes(this.#tier)) {
      this.#scene.burstWind(1.35);
    }
    document.querySelectorAll(".wind-wave").forEach((item) => item.remove());
    this.#clearWindSection();

    const activeSection =
      document.body.dataset.activeSection ??
      this.#store.get().section ??
      "hero";
    this.#windSection = [...document.querySelectorAll("[data-section]")]
      .find((section) => section.dataset.section === activeSection) ?? null;
    this.#windSection?.classList.add("is-wind-reacting");
    document.body.dataset.windSection = activeSection;
    document.body.classList.add("wind-active");
    this.#windButton.classList.remove("is-active");
    void this.#windButton.offsetWidth;
    this.#windButton.classList.add("is-active");
    this.#windButton.setAttribute("aria-label", "清风已吹过");
    const label = this.#windButton.querySelector("em");
    if (label) {
      label.textContent = "风起";
    }

    const wave = document.createElement("span");
    wave.className = "wind-wave";
    wave.dataset.windSection = activeSection;
    wave.setAttribute("aria-hidden", "true");
    document.body.append(wave);
    wave.addEventListener("animationend", () => wave.remove(), {
      once: true
    });

    window.clearTimeout(this.#windTimer);
    this.#windTimer = window.setTimeout(() => {
      document.body.classList.remove("wind-active");
      delete document.body.dataset.windSection;
      this.#clearWindSection();
      this.#windButton.classList.remove("is-active");
      this.#windButton.setAttribute("aria-label", "吹起一阵清风");
      if (label) {
        label.textContent = "清风";
      }
    }, 1200);
    this.#store.emit("ambient:wind", { section: activeSection });
  }

  #clearWindSection() {
    this.#windSection?.classList.remove("is-wind-reacting");
    this.#windSection = null;
  }

  #toggleMist() {
    this.#mistDense = !this.#mistDense;
    document.body.classList.toggle("mist-dense", this.#mistDense);
    this.#mistButton.setAttribute(
      "aria-pressed",
      String(this.#mistDense)
    );
    const label = this.#mistButton.querySelector("em");
    if (label) {
      label.textContent = this.#mistDense ? "CLEAR MIST" : "SOFT MIST";
    }
  }

  #resetIdle() {
    document.body.classList.remove("is-idle");
    window.clearTimeout(this.#idleTimer);
    this.#idleTimer = window.setTimeout(() => {
      document.body.classList.add("is-idle");
      this.#store.emit("ambient:idle");
    }, 15000);
  }

  destroy() {
    this.#cleanup.forEach((cleanup) => cleanup());
    window.clearTimeout(this.#idleTimer);
    window.clearTimeout(this.#windTimer);
    delete document.body.dataset.windSection;
    this.#clearWindSection();
    document.body.classList.remove(
      "is-idle",
      "mist-dense",
      "wind-active"
    );
    this.#windButton.classList.remove("is-active");
  }
}
